import { WarehouseBin } from '../../services/inventory/types';
import { EXCEPTION_TYPES } from '../../services/fulfillment/constants';
import {
  BinLocation,
  BinLocationType,
  FulfillmentLineExceptionType,
  FulfillmentLineStatus,
  LineItem,
  LineItemLocalState,
  OrderDetail,
} from '../../services/fulfillment/types';

export type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';
export type ChipVariant = 'filled' | 'outlined';

export function buildInitialLineStatuses(
  orderData: OrderDetail,
): Record<string, LineItemLocalState> {
  const initial: Record<string, LineItemLocalState> = {};
  for (const li of orderData.lineItems) {
    initial[li.shopifyLineItemGid] = {
      status: li.lineStatus ?? 'pending',
      fulfillmentBinCode: li.fulfillmentBinCode,
      exceptionType: li.exceptionType,
      notes: li.exceptionNotes,
      pendingStatus: null,
      pendingBin: null,
      pendingBinTypeahead: '',
      pendingExceptionType: null,
      pendingNotes: null,
    };
  }
  return initial;
}

export function resolveQcZoneTypes(orderData: OrderDetail): string[] {
  return orderData.qcAllowRefurbishedZone ? ['qc', 'refurbished'] : ['qc'];
}

export function partitionLines(
  lineItems: LineItem[],
  lineStatuses: Record<string, LineItemLocalState>,
): { activeLines: LineItem[]; removedLines: LineItem[] } {
  const activeLines = lineItems.filter(
    li => (lineStatuses[li.shopifyLineItemGid]?.status ?? li.lineStatus) !== 'removed',
  );
  const removedLines = lineItems.filter(li => li.lineStatus === 'removed');
  return { activeLines, removedLines };
}

export function getEffectiveStatus(ls: LineItemLocalState): FulfillmentLineStatus {
  return ls.pendingStatus ?? ls.status;
}

/**
 * Filter for the location cell display: which bin locationTypes should we list
 * as candidate locations for an item still in pre-pick states?
 */
export function pickLocationFilter(
  effectiveStatus: FulfillmentLineStatus,
  qcZoneTypes: string[],
): string[] {
  if (effectiveStatus === 'quality-check') return qcZoneTypes;
  return ['pick', 'refurbished'];
}

/**
 * Filter for the editor's bin autocomplete options, given the user's pending
 * status choice.
 */
export function pickEditorBinFilter(
  pendingStatus: FulfillmentLineStatus,
  qcZoneTypes: string[],
): string[] {
  if (pendingStatus === 'quality-check') return qcZoneTypes;
  if (pendingStatus === 'picked' || pendingStatus === 'packed') return ['fulfillment'];
  return [];
}

export function filterBinsForStatus(
  binLocations: BinLocation[],
  effectiveStatus: FulfillmentLineStatus,
  qcZoneTypes: string[],
): BinLocation[] {
  const allowed = pickLocationFilter(effectiveStatus, qcZoneTypes);
  return binLocations.filter(b => allowed.includes(b.locationType as BinLocationType));
}

export function getExceptionLabel(excType: FulfillmentLineExceptionType | null): string {
  if (!excType) return '—';
  return EXCEPTION_TYPES.find(e => e.value === excType)?.label ?? excType;
}

export function formatAddressLines(shippingAddress: OrderDetail['shippingAddress']): string[] {
  if (!shippingAddress) return [];
  return [
    shippingAddress.address1,
    shippingAddress.address2,
    `${shippingAddress.city}, ${shippingAddress.province} ${shippingAddress.zip}`,
  ].filter((line): line is string => Boolean(line));
}

export function formatCustomerName(customer: OrderDetail['customer']): string | null {
  if (!customer) return null;
  return `${customer.firstName} ${customer.lastName}`.trim();
}

export function riskLevelChipProps(riskLevel: string): { color: ChipColor; variant: ChipVariant } {
  const elevated = ['MEDIUM', 'HIGH'].includes(riskLevel.toUpperCase());
  return {
    color: elevated ? 'error' : 'success',
    variant: elevated ? 'filled' : 'outlined',
  };
}

export function toBinOption(bin: { id: number; code: string }): WarehouseBin {
  return ({ id: bin.id, code: bin.code } as unknown) as WarehouseBin;
}
